import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SelfAssessmentQuiz = () => {
    const { t } = useTranslation();
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [showResults, setShowResults] = useState(false);

    const questions = t('quiz.questions', { returnObjects: true }) || [];

    const handleAnswerSelect = (score) => {
        const newAnswers = [...answers];
        newAnswers[currentQuestion] = score;
        setAnswers(newAnswers);

        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        } else {
            setShowResults(true);
        }
    };

    const calculateScore = () => {
        return answers.reduce((total, score) => total + score, 0);
    };

    const getResultMessage = (score) => {
        const results = t('quiz.results', { returnObjects: true });
        if (score <= 5) {
            return {
                level: results.minimal.level,
                message: results.minimal.message,
                color: "text-sage-700 dark:text-sage-300",
                badgeBg: "bg-sage-100 text-sage-800 dark:bg-stone-800 dark:text-sage-300 border-sage-200 dark:border-stone-700",
            };
        } else if (score <= 10) {
            return {
                level: results.mild.level,
                message: results.mild.message,
                color: "text-amber-700 dark:text-amber-300",
                badgeBg: "bg-amber-100 text-amber-800 dark:bg-stone-800 dark:text-amber-300 border-amber-200 dark:border-stone-700",
            };
        } else if (score <= 15) {
            return {
                level: results.moderate.level,
                message: results.moderate.message,
                color: "text-orange-700 dark:text-orange-300",
                badgeBg: "bg-orange-100 text-orange-800 dark:bg-stone-800 dark:text-orange-300 border-orange-200 dark:border-stone-700",
            };
        } else {
            return {
                level: results.severe.level,
                message: results.severe.message,
                color: "text-terracotta dark:text-terracotta-light",
                badgeBg: "bg-terracotta-100 text-terracotta-800 dark:bg-stone-800 dark:text-terracotta-light border-terracotta-200 dark:border-stone-700",
            };
        }
    };

    const resetQuiz = () => {
        setCurrentQuestion(0);
        setAnswers([]);
        setShowResults(false);
    };

    if (!Array.isArray(questions) || questions.length === 0) {
        return null;
    }

    if (showResults) {
        const score = calculateScore();
        const result = getResultMessage(score);
        const maxScore = questions.length * 3;

        return (
            <div className="max-w-2xl mx-auto w-full">
                <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 text-center transition-all duration-300">
                    
                    {/* Badge */}
                    <div className="mb-6">
                        <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${result.badgeBg}`}>
                            {result.level} • Scor: {score}/{maxScore}
                        </span>
                    </div>

                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                        {t('quiz.ui.your_results')}
                    </h3>
                    
                    <p className="text-stone-600 dark:text-stone-300 text-base sm:text-lg leading-relaxed mb-8 max-w-xl mx-auto">
                        {result.message}
                    </p>

                    {/* Recommendations box */}
                    <div className="bg-stone-50 dark:bg-stone-800/60 rounded-2xl p-6 sm:p-8 mb-8 text-left border border-stone-200/60 dark:border-stone-700">
                        <h4 className="font-bold text-stone-900 dark:text-stone-100 mb-4 flex items-center text-base sm:text-lg">
                            <svg className="w-5 h-5 text-terracotta mr-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {t('quiz.ui.recommendations')}
                        </h4>
                        <ul className="space-y-2.5 text-sm text-stone-600 dark:text-stone-300">
                            <li className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2.5 mt-1.5 flex-shrink-0"></span>
                                {t('quiz.ui.rec1')}
                            </li>
                            <li className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2.5 mt-1.5 flex-shrink-0"></span>
                                {t('quiz.ui.rec2')}
                            </li>
                            <li className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2.5 mt-1.5 flex-shrink-0"></span>
                                {t('quiz.ui.rec3')}
                            </li>
                            <li className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2.5 mt-1.5 flex-shrink-0"></span>
                                {t('quiz.ui.rec4')}
                            </li>
                        </ul>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-7 py-3.5 bg-terracotta hover:bg-terracotta-dark text-white font-semibold rounded-full shadow-warm hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 text-sm sm:text-base"
                        >
                            Programează o consultație
                        </Link>
                        <button
                            onClick={resetQuiz}
                            className="inline-flex items-center justify-center px-6 py-3.5 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-300 font-semibold rounded-full transition-colors text-sm"
                        >
                            {t('quiz.ui.retake')}
                        </button>
                    </div>

                    <p className="text-xs text-stone-400 dark:text-stone-500 mt-6">
                        {t('quiz.ui.disclaimer')}
                    </p>
                </div>
            </div>
        );
    }

    const currentQ = questions[currentQuestion];
    const progressPercent = ((currentQuestion + 1) / questions.length) * 100;

    return (
        <div className="max-w-2xl mx-auto w-full">
            <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 transition-all duration-300">
                
                {/* Progress bar */}
                <div className="mb-8">
                    <div className="flex justify-between items-center text-xs font-semibold text-stone-500 dark:text-stone-400 mb-2 uppercase tracking-wider">
                        <span>Întrebarea {currentQuestion + 1} din {questions.length}</span>
                        <span>{Math.round(progressPercent)}%</span>
                    </div>
                    <div className="w-full bg-stone-100 dark:bg-stone-800 h-2 rounded-full overflow-hidden">
                        <div 
                            className="bg-gradient-to-r from-terracotta to-warm-orange h-full rounded-full transition-all duration-300"
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                    </div>
                </div>

                {/* Question */}
                <h3 className="font-display text-xl sm:text-2xl font-bold text-stone-900 dark:text-stone-100 mb-8 leading-snug">
                    {currentQ.question}
                </h3>

                {/* Options */}
                <div className="space-y-3 mb-8">
                    {currentQ.options.map((option, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleAnswerSelect(option.score)}
                            className="w-full text-left p-4 sm:p-5 rounded-2xl border border-stone-200/90 dark:border-stone-700/80 bg-stone-50/60 dark:bg-stone-800/40 hover:bg-terracotta-50 dark:hover:bg-stone-800 hover:border-terracotta-300 dark:hover:border-terracotta transition-all duration-200 flex items-center justify-between group"
                        >
                            <span className="text-sm sm:text-base font-medium text-stone-800 dark:text-stone-200 group-hover:text-terracotta transition-colors">
                                {option.text}
                            </span>
                            <span className="w-5 h-5 rounded-full border border-stone-300 dark:border-stone-600 group-hover:border-terracotta group-hover:bg-terracotta/10 flex items-center justify-center flex-shrink-0 ml-4 transition-colors">
                                <span className="w-2 h-2 rounded-full bg-transparent group-hover:bg-terracotta transition-colors"></span>
                            </span>
                        </button>
                    ))}
                </div>

                {/* Back / Navigation button */}
                {currentQuestion > 0 && (
                    <button
                        onClick={() => setCurrentQuestion(currentQuestion - 1)}
                        className="text-xs font-semibold text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 transition-colors flex items-center"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Întrebarea precedentă
                    </button>
                )}
            </div>
        </div>
    );
};

export default SelfAssessmentQuiz;
